import React from 'react';
import { ClipLoader } from 'react-spinners';
import { 
    FaEdit, 
    FaTrashAlt, 
    FaCheckCircle, 
    FaFilePdf, 
    FaFileInvoiceDollar, 
    FaTools, 
    FaCopy,
    FaFileContract 
} from 'react-icons/fa';
import PresupuestoPDFLink from '../PresupuestoPDFLink'; 

const ListaPresupuestos = ({ 
    presupuestos, 
    cargando, 
    busqueda, 
    setBusqueda, 
    onEdit, 
    onDelete, 
    onApprove, 
    onCopy, 
    isApprovedView, 
    isOrderView 
}) => {

    if (cargando) {
        return (
            <div className="bg-white p-12 rounded-[2rem] shadow-sm border border-gray-100 mt-2 flex flex-col items-center justify-center">
                <ClipLoader color="#1BB1A5" size={40} />
                <p className="text-gray-400 mt-4 text-xs font-bold uppercase tracking-widest">Sincronizando Base de Datos...</p>
            </div>
        );
    }

    const getStatusBadge = (status) => {
        const currentStatus = status || 'Borrador';
        switch (currentStatus) {
            case 'Aprobado':
                return <span className="px-3 py-1 text-[10px] font-black uppercase tracking-widest text-kokimiko-verde bg-kokimiko-verde/10 rounded-lg">Aprobado</span>;
            case 'Rechazado':
                return <span className="px-3 py-1 text-[10px] font-black uppercase tracking-widest text-kokimiko-naranja bg-kokimiko-naranja/10 rounded-lg">Rechazado</span>;
            case 'Pendiente':
                return <span className="px-3 py-1 text-[10px] font-black uppercase tracking-widest text-kokimiko-azul bg-kokimiko-azul/10 rounded-lg">Pendiente</span>;
            default:
                return <span className="px-3 py-1 text-[10px] font-black uppercase tracking-widest text-gray-500 bg-gray-100 rounded-lg">Borrador</span>;
        }
    };

    const obtenerNombreCliente = (pres) => {
        if (!pres) return 'Sin Datos';
        if (pres.cliente) {
            if (typeof pres.cliente === 'object') {
                return pres.cliente.name || pres.cliente.nombreRepresentante || pres.cliente.razonSocial || 'Sin Nombre';
            }
            if (typeof pres.cliente === 'string') return pres.cliente;
        }
        if (pres.datosCliente && (pres.datosCliente.name || pres.datosCliente.nombreRepresentante)) {
            return pres.datosCliente.name || pres.datosCliente.nombreRepresentante;
        }
        if (pres.nombreCliente) return pres.nombreCliente;
        return 'Sin Cliente';
    };

    // 🔥 LECTURA LIMPIA DEL TOTAL (ESTRUCTURA NUEVA EVENTOS) 🔥
    const obtenerMontoTotal = (pres) => {
        let total = 0;
        
        // 1. Intenta leer de la estructura nueva de eventos
        if (pres.calculos && pres.calculos.totalGeneral) {
            total = parseFloat(pres.calculos.totalGeneral);
        } 
        // 2. Fallback a la estructura guardada previamente en totales
        else if (pres.totales && pres.totales.totalGeneral) {
            total = parseFloat(pres.totales.totalGeneral);
        }
        // 3. Fallback genérico
        else if (pres.totalGeneral) {
            total = parseFloat(pres.totalGeneral);
        }

        return (total || 0).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' });
    };

    const presupuestosValidos = Array.isArray(presupuestos) ? presupuestos : [];
    
    // Título dinámico pero con la estética nueva
    let titleIcon = <FaFileInvoiceDollar />;
    let titleText = "Borradores y Cotizaciones Enviadas";
    if (isApprovedView) {
        titleIcon = <FaCheckCircle />;
        titleText = "Cotizaciones Aprobadas";
    } else if (isOrderView) {
        titleIcon = <FaFileContract />;
        titleText = "Historial de Órdenes";
    }

    return (
        <div className="bg-white p-6 rounded-b-[2rem] rounded-tr-[2rem] shadow-sm border border-gray-100 mt-2">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4 border-b border-gray-100 pb-4">
                <h3 className="text-lg font-black text-kokimiko-texto uppercase tracking-widest flex items-center gap-2">
                    <span className="text-kokimiko-teal">{titleIcon}</span>
                    {titleText}
                </h3>
                <input
                    type="text"
                    placeholder="Buscar por familia o referencia..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="w-full sm:w-72 p-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-kokimiko-teal focus:outline-none text-sm font-bold text-kokimiko-texto transition-all"
                />
            </div>

            <div className="overflow-x-auto custom-scrollbar">
                <table className="w-full text-sm text-left text-gray-600">
                    <thead className="text-[10px] text-gray-400 uppercase tracking-widest bg-gray-50 border-b border-gray-100 font-black">
                        <tr>
                            <th scope="col" className="px-6 py-4 rounded-tl-xl">{isOrderView ? 'Nº Pedido' : 'Referencia'}</th>
                            <th scope="col" className="px-6 py-4">Fecha</th>
                            <th scope="col" className="px-6 py-4">Familia / Cliente</th>
                            <th scope="col" className="px-6 py-4 text-right">Total Presupuestado</th>
                            <th scope="col" className="px-6 py-4 text-center">Estado</th>
                            <th scope="col" className="px-6 py-4 text-center rounded-tr-xl">Acciones</th>
                        </tr>
                    </thead>
                    <tbody>
                        {presupuestosValidos.length > 0 ? (
                            presupuestosValidos.map((pres) => {
                                const esBorradorOPendiente = !pres.estado || pres.estado === 'Borrador' || pres.estado === 'Pendiente';

                                return (
                                    <tr key={pres.id} className="border-b border-gray-50 hover:bg-kokimiko-teal/5 transition-colors">
                                        <td className="px-6 py-4 font-black text-gray-400">
                                            {isOrderView ? (pres.pedidoVinculado || 'N/A') : (pres.numero || 'N/A')}
                                        </td>
                                        <td className="px-6 py-4 font-bold text-gray-500">
                                            {pres.fecha ? new Date(pres.fecha).toLocaleDateString('es-ES') : 'N/A'}
                                        </td>
                                        <td className="px-6 py-4 font-black text-kokimiko-texto">
                                            {obtenerNombreCliente(pres)}
                                        </td> 
                                        
                                        <td className="px-6 py-4 text-right font-black text-kokimiko-teal text-base">
                                            {obtenerMontoTotal(pres)}
                                        </td>

                                        <td className="px-6 py-4 text-center">
                                            {getStatusBadge(pres.estado)}
                                        </td>
                                        
                                        <td className="px-6 py-4 text-center">
                                            <div className="flex justify-center items-center gap-2">
                                                
                                                {/* Botón de Aprobar */}
                                                {!isApprovedView && !isOrderView && onApprove && esBorradorOPendiente && (
                                                    <button 
                                                        onClick={() => onApprove(pres.id)} 
                                                        className="p-2 bg-kokimiko-verde/10 text-kokimiko-verde hover:bg-kokimiko-verde hover:text-white rounded-lg transition-colors" 
                                                        title="Aprobar Cotización"
                                                    >
                                                        <FaCheckCircle size={16} />
                                                    </button>
                                                )}
                                                
                                                {/* BOTÓN PDF CLIENTE / ORDEN */}
                                                <div title={isOrderView ? "Descargar Orden de Compra" : "Descargar Cotización (Cliente)"}>
                                                    <PresupuestoPDFLink 
                                                        presupuesto={pres} 
                                                        type={isOrderView ? "orden" : "presupuesto"} 
                                                        icon={
                                                            <span className="flex items-center gap-2 px-3 py-2 bg-red-50 text-red-500 hover:bg-red-500 hover:text-white rounded-lg transition-colors text-xs font-black uppercase tracking-widest cursor-pointer whitespace-nowrap">
                                                                <FaFilePdf size={14} /> {isOrderView ? "Orden" : "PDF"}
                                                            </span>
                                                        } 
                                                    />
                                                </div>

                                                {/* Botones de acción extra (Copiar / Editar / Eliminar) */}
                                                {!isOrderView && (
                                                    <div className="flex gap-1 ml-2 pl-3 border-l border-gray-100">
                                                        {onCopy && (
                                                            <button 
                                                                onClick={() => onCopy(pres)} 
                                                                className="p-2 text-gray-400 hover:text-kokimiko-azul hover:bg-kokimiko-azul/10 rounded-lg transition-colors" 
                                                                title="Duplicar (Crear Borrador)"
                                                            >
                                                                <FaCopy size={14} />
                                                            </button>
                                                        )}

                                                        {onEdit && (
                                                            <button 
                                                                onClick={() => onEdit(pres)} 
                                                                className="p-2 text-gray-400 hover:text-kokimiko-texto hover:bg-gray-100 rounded-lg transition-colors" 
                                                                title="Editar Cotización"
                                                            >
                                                                <FaEdit size={14} />
                                                            </button>
                                                        )}
                                                        
                                                        <button 
                                                            onClick={() => onDelete(pres.id)} 
                                                            className="p-2 text-gray-400 hover:text-white hover:bg-red-500 rounded-lg transition-colors" 
                                                            title="Eliminar Documento"
                                                        >
                                                            <FaTrashAlt size={14} />
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })
                        ) : (
                            <tr>
                                <td colSpan="6" className="text-center py-12 text-gray-400 font-bold text-xs uppercase tracking-widest">
                                    No hay cotizaciones en esta bandeja
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default ListaPresupuestos;